import { formatDistanceToNow } from 'date-fns'
import { useState } from 'react'
import { useAudienceContacts, useRemoveContacts } from '@/api/hooks/use-audiences'
import { DataTable } from '@/components/ui/data-table'
import { Modal } from '@/components/ui/modal'
import { StatusBadge } from '@/components/ui/status-badge'
import type { AudienceContact } from '@/types/newsletter'

const STATUS_OPTIONS = ['all', 'active', 'unsubscribed', 'bounced', 'cleaned'] as const

interface AudienceContactsTabProps {
  audienceId: string
}

export function AudienceContactsTab({ audienceId }: AudienceContactsTabProps): React.JSX.Element {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [removeTarget, setRemoveTarget] = useState<string | null>(null)

  const removeContacts = useRemoveContacts()

  const { data, isLoading } = useAudienceContacts(audienceId, {
    page,
    per_page: 25,
    search: search || undefined,
    status: statusFilter === 'all' ? undefined : statusFilter,
  })

  const contacts = data?.data ?? []
  const pagination = data?.pagination

  function handleRemoveSingle(): void {
    if (!removeTarget) return
    removeContacts.mutate(
      { audience_id: audienceId, emails: [removeTarget] },
      { onSuccess: () => setRemoveTarget(null) },
    )
  }

  function handleRemoveSelected(): void {
    const emails = contacts.filter((c) => selected.has(c.id)).map((c) => c.email)
    if (emails.length === 0) return
    removeContacts.mutate(
      { audience_id: audienceId, emails },
      { onSuccess: () => setSelected(new Set()) },
    )
  }

  function toggleSelect(id: string): void {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  function toggleSelectAll(): void {
    if (selected.size === contacts.length) {
      setSelected(new Set())
    } else {
      setSelected(new Set(contacts.map((c) => c.id)))
    }
  }

  const columns = [
    {
      key: 'select',
      label: '',
      render: (row: AudienceContact) => (
        <input
          type="checkbox"
          checked={selected.has(row.id)}
          onChange={() => toggleSelect(row.id)}
          className="h-4 w-4 rounded border-surface-3"
          onClick={(e) => e.stopPropagation()}
        />
      ),
    },
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      render: (row: AudienceContact) => <span className="font-medium">{row.email}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      render: (row: AudienceContact) => <StatusBadge status={row.status}>{row.status}</StatusBadge>,
    },
    {
      key: 'metadata',
      label: 'Metadata',
      render: (row: AudienceContact) => {
        const json = JSON.stringify(row.metadata)
        return (
          <span className="max-w-[200px] truncate text-xs text-text-tertiary" title={json}>
            {json === '{}' ? '-' : json}
          </span>
        )
      },
    },
    {
      key: 'subscribed_at',
      label: 'Subscribed',
      render: (row: AudienceContact) => (
        <span className="text-sm text-text-secondary">
          {formatDistanceToNow(new Date(row.subscribed_at), { addSuffix: true })}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '',
      render: (row: AudienceContact) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            setRemoveTarget(row.email)
          }}
          className="text-xs text-red-500 hover:text-red-700"
        >
          Remove
        </button>
      ),
    },
  ]

  return (
    <div className="space-y-4">
      {/* Search + filter bar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <input
            type="text"
            placeholder="Search by email..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
            className="w-64 rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-brand-500 focus:outline-none"
          />
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value)
              setPage(1)
            }}
            className="rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-sm text-text-primary focus:border-brand-500 focus:outline-none"
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s === 'all' ? 'All statuses' : s}
              </option>
            ))}
          </select>
        </div>
        {selected.size > 0 && (
          <button
            type="button"
            onClick={handleRemoveSelected}
            disabled={removeContacts.isPending}
            className="rounded-lg border border-red-300 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
          >
            Remove {selected.size} selected
          </button>
        )}
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={contacts.length > 0 && selected.size === contacts.length}
          onChange={toggleSelectAll}
          className="h-4 w-4 rounded border-surface-3"
        />
        <span className="text-xs text-text-tertiary">Select all on this page</span>
      </div>

      <DataTable
        columns={columns}
        data={contacts}
        isLoading={isLoading}
        emptyMessage="No contacts found"
        pagination={
          pagination
            ? { page: pagination.page, perPage: pagination.per_page, total: pagination.total }
            : undefined
        }
        onPageChange={setPage}
      />

      {/* Remove confirmation modal */}
      <Modal
        open={!!removeTarget}
        onClose={() => setRemoveTarget(null)}
        title="Remove Contact"
        size="sm"
      >
        <p className="text-sm text-text-secondary">
          Are you sure you want to remove <strong>{removeTarget}</strong> from this audience?
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setRemoveTarget(null)}
            className="rounded-lg border border-surface-3 px-4 py-2 text-sm font-medium text-text-primary hover:bg-surface-1"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleRemoveSingle}
            disabled={removeContacts.isPending}
            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            {removeContacts.isPending ? 'Removing...' : 'Remove'}
          </button>
        </div>
      </Modal>
    </div>
  )
}
